import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Star, MessageSquare, Wallet, Tag, Settings, LogOut,
  ChevronDown, ChevronRight, FileText, ChevronLeft, Layers, Headphones,
  Percent, Star as ReviewStar, CreditCard, ArrowLeftRight, Bell, TrendingUp,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import brandMark from '../../assets/branding/astrovrinda-mark.png';

interface NavChild {
  label: string;
  path: string;
}

interface NavItem {
  label: string;
  icon: React.ElementType;
  path?: string;
  children?: NavChild[];
}

/** PDF §41 sidebar — filter sub-items share a page via query params */
const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  {
    label: 'Users', icon: Users, children: [
      { label: 'All', path: '/users' },
      { label: 'Active', path: '/users?status=active' },
      { label: 'Blocked', path: '/users?status=blocked' },
    ],
  },
  {
    label: 'Astrologers', icon: Star, children: [
      { label: 'All', path: '/astrologers' },
      { label: 'Pending Applications', path: '/astrologers/applications' },
      { label: 'KYC', path: '/astrologers/kyc' },
      { label: 'Approved', path: '/astrologers?approval=approved' },
      { label: 'Rejected', path: '/astrologers?approval=rejected' },
      { label: 'Active', path: '/astrologers?active=1' },
      { label: 'Inactive', path: '/astrologers?active=0' },
    ],
  },
  { label: 'Services & Categories', icon: Layers, path: '/content/categories' },
  {
    label: 'Consultations', icon: MessageSquare, children: [
      { label: 'All', path: '/consultations' },
      { label: 'Chat', path: '/consultations?type=chat' },
      { label: 'Audio', path: '/consultations?type=call' },
      { label: 'Video', path: '/consultations?type=video' },
      { label: 'Appointments', path: '/consultations/appointments' },
      { label: 'Emergency', path: '/consultations/emergency' },
    ],
  },
  { label: 'Wallet & Payments', icon: Wallet, path: '/finance/payments' },
  { label: 'Transactions', icon: ArrowLeftRight, path: '/finance/transactions' },
  { label: 'Revenue', icon: TrendingUp, path: '/finance/revenue' },
  { label: 'Withdrawals', icon: CreditCard, path: '/finance/withdrawals' },
  { label: 'Commission', icon: Percent, path: '/finance/commission' },
  { label: 'Wallet Recharge Packs', icon: Wallet, path: '/finance/packages' },
  { label: 'Coupons & Offers', icon: Tag, path: '/offers' },
  { label: 'Reviews', icon: ReviewStar, path: '/reviews' },
  { label: 'Support Tickets', icon: Headphones, path: '/support' },
  { label: 'Notifications', icon: Bell, path: '/notifications' },
  {
    label: 'CMS', icon: FileText, children: [
      { label: 'Banners', path: '/content/banners' },
      { label: 'Pages', path: '/cms/pages' },
      { label: 'FAQs', path: '/content/faqs' },
      { label: 'CEO Profile', path: '/cms/ceo-profile' },
      { label: 'Tarot by Vrinda', path: '/cms/tarot-by-vrinda' },
    ],
  },
  { label: 'Reports', icon: FileText, path: '/reports' },
  {
    label: 'Settings', icon: Settings, children: [
      { label: 'General', path: '/settings' },
      { label: 'Integrations', path: '/settings/integrations' },
      { label: 'Payment Gateways', path: '/finance/gateways' },
      { label: 'Languages', path: '/language/languages' },
      { label: 'Translations', path: '/language/translations' },
      { label: 'Feature controls', path: '/settings/features' },
      { label: 'Activity Logs', path: '/settings/activity-logs' },
    ],
  },
];

function pathMatches(locationPath: string, locationSearch: string, target: string) {
  const [tPath, tQuery = ''] = target.split('?');
  if (locationPath !== tPath && !locationPath.startsWith(tPath + '/')) {
    // exact path match preferred for list filters
    if (locationPath !== tPath) return false;
  }
  if (locationPath !== tPath) {
    // detail routes under same prefix shouldn't light every filter
    return false;
  }
  if (!tQuery) {
    // bare path is active when no filter query (or only empty)
    return !locationSearch || locationSearch === '?';
  }
  const want = new URLSearchParams(tQuery);
  const have = new URLSearchParams(locationSearch);
  let ok = true;
  want.forEach((v, k) => {
    if (have.get(k) !== v) ok = false;
  });
  return ok;
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    const [p] = path.split('?');
    return location.pathname === p || location.pathname.startsWith(p + '/');
  };

  const isGroupActive = (item: NavItem) =>
    item.children?.some((c) => {
      const [p] = c.path.split('?');
      return location.pathname === p || location.pathname.startsWith(p + '/');
    });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className={`flex flex-col h-screen bg-cream-50 border-r border-cream-300 transition-all duration-200 ${collapsed ? 'w-[64px]' : 'w-[240px]'} flex-shrink-0`}>
      <div className={`flex items-center h-16 px-4 border-b border-cream-300 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2 min-w-0">
            <img src={brandMark} alt="AstroVrinda" className="h-9 w-9 rounded-full object-cover bg-ink" />
            <span className="text-sm font-bold text-ink truncate">AstroVrinda</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary-700 bg-primary-100 rounded px-1.5 py-0.5">Admin</span>
          </div>
        )}
        {collapsed && <img src={brandMark} alt="AstroVrinda" className="w-8 h-8 rounded-full object-cover bg-ink" />}
        <button onClick={() => setCollapsed(!collapsed)} className="p-1 rounded hover:bg-cream-200 text-ink/60">
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          if (item.children) {
            const active = isGroupActive(item);
            const open = openGroups[item.label] || active;
            return (
              <div key={item.label}>
                <button
                  onClick={() => !collapsed && toggleGroup(item.label)}
                  className={`sidebar-link w-full ${active ? 'text-primary-600 bg-primary-50' : 'sidebar-link-inactive'}`}
                >
                  <item.icon size={18} className="flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </>
                  )}
                </button>
                {!collapsed && open && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                    {item.children.map((child) => {
                      const childActive = pathMatches(location.pathname, location.search, child.path);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`sidebar-link text-xs ${childActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path!}
              className={`sidebar-link ${isActive(item.path) ? 'sidebar-link-active' : 'sidebar-link-inactive'}`}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={18} className="flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-cream-300 p-3">
        <div className={`flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-sm flex-shrink-0">
            {admin?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-ink truncate">{admin?.name}</p>
              <p className="text-xs text-ink/50 capitalize">{admin?.role}</p>
            </div>
          )}
          <button onClick={handleLogout} className="p-1 rounded hover:bg-primary-50 text-ink/40 hover:text-primary-700" title="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
